"use client";

import { useCallback, useState } from "react";
import { Card, CardContent, CardHeader, Grid, TextField } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import { LookupFormProps } from "@/types/lookup";
import { UserBase } from "@/types/user";
import {
  DateValidationProps,
  LookupValidation,
  ValidationParams,
} from "@/types/validation";

import { handleChange } from "@/lib/validation";
import { lookupValidation } from "@/lib/validation/lookup";

import { useUi } from "@/context/util/ui";

import ExtendedDatePicker from "@/components/wrapper/extendedDatePicker";

export default function LookupForm<T extends UserBase>(
  props: LookupFormProps<T>
) {
  const { isMobileDevice } = useUi();

  const [birthdate, setBirthdate] = useState<Dayjs | null>(
    props.entity?.birthdate ? dayjs(props.entity?.birthdate) : null
  );

  const onChangeParams = useCallback<
    () => Pick<
      ValidationParams<T, LookupValidation>,
      "validation" | "setDate" | "setEntity" | "setErrors"
    >
  >(() => {
    return {
      validation: lookupValidation,
      setDate: setBirthdate,
      setEntity: props.set,
      setErrors: props.setErrors,
    };
  }, [props.set, props.setErrors]);

  function onChange(
    value: string | Dayjs | null,
    field: keyof LookupValidation
  ) {
    const { validation, setDate, setEntity, setErrors } = onChangeParams();

    handleChange<T, LookupValidation>({
      field,
      setDate,
      setEntity,
      setErrors,
      validation,
      value,
    });
  }

  return (
    <Grid container mb={2}>
      <Grid
        component={Card}
        key="user-lookup-grid"
        size={{ xs: 24 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          padding: "0 0 2em",
        }}
      >
        <CardHeader
          slotProps={{ title: { fontWeight: "bold", color: "primary" } }}
          title={props.title}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Grid container columnSpacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                error={props.errors.firstName !== undefined}
                fullWidth
                helperText={props.errors.firstName}
                key="first-name-value"
                label="First Name"
                onChange={(event) => onChange(event.target.value, "firstName")}
                sx={{ mr: 2 }}
                value={props.entity?.firstName ?? ""}
                variant="standard"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                error={props.errors.lastName !== undefined}
                fullWidth
                helperText={props.errors.lastName}
                key="last-name-value"
                label="Last Name"
                onChange={(event) => onChange(event.target.value, "lastName")}
                sx={{ mr: 2 }}
                value={props.entity?.lastName ?? ""}
                variant="standard"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ExtendedDatePicker
                inputFormat="MM/DD/YYYY"
                isMobileDevice={isMobileDevice}
                key="birthdate-value"
                label="Birthdate"
                maxDate={
                  (lookupValidation.birthdate[0] as DateValidationProps).maxDate
                }
                minDate={
                  (lookupValidation.birthdate[0] as DateValidationProps).minDate
                }
                onChange={(event) =>
                  onChange(event as Dayjs | null, "birthdate")
                }
                slotProps={{
                  textField: {
                    error: props.errors.birthdate !== undefined,
                    fullWidth: true,
                    helperText: props.errors.birthdate,
                    sx: { mb: 2, mr: 2 },
                    variant: "standard",
                  },
                }}
                value={birthdate}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                error={props.errors.zip !== undefined}
                fullWidth
                helperText={props.errors.zip}
                key="zip-value"
                label="Zip Code"
                onChange={(event) => onChange(event.target.value, "zip")}
                sx={{ mr: 2 }}
                value={props.entity?.zip ?? ""}
                variant="standard"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Grid>
    </Grid>
  );
}
